// ./src/components/generic/FinancingStatusChipSelector/FinancingStatusChipSelector.tsx
//
// Chip-based single-select for financing status (Active / Completed).
// Uses Mantine Chip.Group for a compact, touch-friendly UI.
// Supports clearable selection (clicking the active chip deselects it).
//
// Styling matches TravelTypeSelector pattern.

import React, { useMemo, useCallback } from "react";
import { Chip, Input, Box } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { VEHICLE_FINANCING_STATUS } from "@/utils/api";
import { AppIcon } from "@/components/generic";

// ============================================
// Types
// ============================================

/** Status values as used by the API */
export type FinancingStatusValue =
  | typeof VEHICLE_FINANCING_STATUS.ACTIVE
  | typeof VEHICLE_FINANCING_STATUS.COMPLETED;

export interface FinancingStatusChipSelectorProps {
  /** Currently selected value (null = all / none selected) */
  value: FinancingStatusValue | null;

  /** Callback when selection changes */
  onChange: (value: FinancingStatusValue | null) => void;

  /** Optional label displayed above the chips */
  label?: string;

  /** Whether the field is required (disables deselect) */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Disable interaction */
  disabled?: boolean;

  /** Size of the input wrapper */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Chip variant */
  variant?: "outline" | "filled" | "light";
}

// ============================================
// Chip Config
// ============================================

interface ChipConfig {
  value: FinancingStatusValue;
  labelKey: string;
  iconName: string;
  color: string;
}

const CHIP_OPTIONS: ChipConfig[] = [
  {
    value: VEHICLE_FINANCING_STATUS.ACTIVE,
    labelKey: "vehicleFinancing:status.active",
    iconName: "PlayerPlay",
    color: "green",
  },
  {
    value: VEHICLE_FINANCING_STATUS.COMPLETED,
    labelKey: "vehicleFinancing:status.completed",
    iconName: "Check",
    color: "gray",
  },
];

// ============================================
// Styles
// ============================================

const CHIP_LABEL_STYLES = {
  borderRadius: "var(--mantine-radius-md)",
  height: "42px",
  paddingLeft: "12px",
  paddingRight: "14px",
  fontSize: "var(--mantine-font-size-sm)",
  width: "100%",
  justifyContent: "center",
};

const CHIP_ICON_WRAPPER_STYLES = {
  width: "auto",
  height: "20px",
  paddingRight: "4px",
};

const CHIP_ROOT_STYLES = {
  flex: "1 1 0",
  minWidth: 0,
};

const GROUP_STYLES: React.CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--mantine-spacing-xs)",
};

// ============================================
// Component
// ============================================

const FinancingStatusChipSelectorComponent: React.FC<FinancingStatusChipSelectorProps> = ({
  value,
  onChange,
  label,
  required = false,
  error,
  disabled = false,
  size = "md",
  variant = "outline",
}) => {
  const { t } = useTranslation(["vehicleFinancing"]);

  const defaultLabel = label ?? t("vehicleFinancing:filters.status", { defaultValue: "Status" });

  const handleChange = useCallback(
    (selected: string) => {
      if (value !== null && selected === String(value) && !required) {
        onChange(null);
      } else {
        onChange(selected ? (Number(selected) as FinancingStatusValue) : null);
      }
    },
    [value, required, onChange]
  );

  const chips = useMemo(() => {
    return CHIP_OPTIONS.map((option) => (
      <Chip
        key={option.value}
        value={String(option.value)}
        color={option.color}
        variant={variant}
        size="sm"
        disabled={disabled}
        icon={<AppIcon iconName={option.iconName} size={20} />}
        styles={{
          label: CHIP_LABEL_STYLES,
          iconWrapper: CHIP_ICON_WRAPPER_STYLES,
          root: CHIP_ROOT_STYLES,
        }}
      >
        {t(option.labelKey)}
      </Chip>
    ));
  }, [t, variant, disabled]);

  return (
    <Input.Wrapper
      label={defaultLabel}
      required={required}
      error={error}
      size={size}
    >
      <Box mt={4} style={GROUP_STYLES}>
        <Chip.Group
          multiple={false}
          value={value !== null ? String(value) : ""}
          onChange={handleChange}
        >
          {chips}
        </Chip.Group>
      </Box>
    </Input.Wrapper>
  );
};

export const FinancingStatusChipSelector = React.memo(FinancingStatusChipSelectorComponent);